<template>
  <form @submit.prevent="submitReset">
    <FormGroup>
      <Text variant="h1">Set your new password</Text>

      <View direction="column" gap="sm">
        <FormField label="Password" :error="form.fieldErrors('newPassword')">
          <Input
            v-model="form.fields.newPassword"
            name="password"
            type="password"
            placeholder="Create a new password"
            @focus="showPasswordStrength = true"
          />
        </FormField>

        <PasswordStrength
          :show-component="showPasswordStrength"
          :password="form.fields.newPassword"
          @password-status-change="setPasswordValidity"
        />
      </View>

      <FormField label="Confirm password" :error="form.fieldErrors('confirmPassword')">
        <Input
          v-model="form.fields.confirmPassword"
          name="confirmPassword"
          type="password"
          placeholder="Confirm a new password"
        />
      </FormField>

      <Button type="submit" :loading="form.submitting">Set new password</Button>

      <Text variant="h4" align="center">
        <Link color="accent" :to="backToSignInRoute">Back to Log in</Link>
      </Text>
    </FormGroup>
  </form>
</template>

<script setup lang="ts">
import { helpers, required, sameAs } from '@vuelidate/validators';
import { AxiosError } from 'axios';
import { computed, reactive, ref, watch } from 'vue';
import { RouteLocationRaw, useRoute, useRouter } from 'vue-router';

import {
  Button,
  FormField,
  FormGroup,
  Input,
  Link,
  PasswordStrength,
  Text,
  View,
  useAlertsStore,
  useForm,
} from '@tallo/design-system';
import { UserRole } from '@tallo/user';

import { useAuthStore } from '../auth.store';

const props = defineProps<{
  backToSignInRoute: RouteLocationRaw;
  afterLoginRoute: RouteLocationRaw;
  forRole: UserRole;
}>();

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();
const token = route.query.token as string;
const alertsStore = useAlertsStore();
const showPasswordStrength = ref(false);
const passwordValid = ref(false);
const newPassword = computed(() => fields.newPassword);
const fields = reactive({
  newPassword: '',
  confirmPassword: '',
});
const form = useForm<{ newPassword: string; confirmPassword: string }>(fields, {
  newPassword: {
    required,
    strong: () => passwordValid.value,
  },
  confirmPassword: {
    sameAsRaw: helpers.withMessage('Password does not match', sameAs(newPassword)),
  },
});

function setPasswordValidity(valid: boolean) {
  passwordValid.value = valid;
}

async function submitReset() {
  form.submit(async () => {
    try {
      await authStore.confirmPasswordReset({
        token,
        forRole: props.forRole,
        newPassword: form.fields.newPassword,
      });
      router.push(props.afterLoginRoute);
      alertsStore.success('Your password has been reset');
    } catch (error) {
      let message = 'Something went wrong. Please try again later';

      if (error instanceof AxiosError) {
        const website = props.forRole === UserRole.INVESTOR ? 'app.tallo.ai' : 'homebase.tallo.ai';

        switch (error.response?.status) {
          case 403:
            message = 'Your account is not active. Try to sign up again';
            break;

          case 409:
            message = `Reset password at "${website}/auth/reset-password" to continue.`;
            break;
        }
      }

      form.setFieldError('newPassword', message);

      throw error;
    }
  });
}

watch(form.fields, () => form.clearValidationErrors());
</script>

<style scoped lang="scss">
.back-link-container {
  text-align: center;
}
</style>
